/**
 * Who may sit in a DETACHED post group (the Board of Directors box).
 *
 * The Board of Directors is the CEO's peer group: its posts live in the board
 * ROOT — the container the CEO post sits in — and nowhere else
 * (docs/categorized/features/ORG-BOARD-DETACHED-BOARD-OF-DIRECTORS-SPEC.md).
 * The board only draws a detached box for a card on the chart, so a placement
 * anywhere deeper (a section, a department, a chief officer's own executive
 * container) would pull the post out of its container and draw it nowhere.
 * Both the part editor (offers the control) and the board builder (honours the
 * field) ask these two functions, so they can never disagree again.
 */

/** The board root: the part with `id === 1` — the same rule `buildPositionsTreeFromPart` uses. */
export function boardRootPartKey(parts: readonly any[] | null | undefined): string | null {
    const root = (parts || []).find((p) => p?.id === 1);
    return root?.partKey || null;
}

/** True when a part of `partType` whose parent is `parentPartKey` may carry a `detachedGroup`. */
export function mayJoinDetachedGroup(
    partType: string | null | undefined,
    parentPartKey: string | null | undefined,
    rootPartKey: string | null | undefined
): boolean {
    return partType === 'post' && !!rootPartKey && parentPartKey === rootPartKey;
}

/** partKeys of the posts that render in the Board of Directors box (a detached group in the root). */
export function boardSeatPostKeys(parts: readonly any[] | null | undefined): Set<string> {
    const rootKey = boardRootPartKey(parts);
    return new Set(
        (parts || [])
            .filter((p) => p?.detachedGroup && mayJoinDetachedGroup(p.partType, p.parentPartKey, rootKey))
            .map((p) => p.partKey as string)
    );
}
